import React, { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import type { BillData, QuoteSessionEnrichment } from '@/api/types';
import { confirmAddressKey } from '@/hooks/useQuoteSession';
import type { UkAddressParts } from '@/utils/quoteMapper';

export type WidgetQuoteContext = {
  propertyId: string;
  partnerId?: string;
  agencyName?: string;
  score?: string;
};

export type PendingPlanContact = {
  customerName: string;
  customerEmail: string;
  customerPhone?: string;
};

interface QuoteFlowContextType {
  setPendingBillFile: (file: File) => void;
  claimPendingBillFile: () => File | null;
  setPendingBillData: (data: BillData) => void;
  claimPendingBillData: () => BillData | null;
  hasPendingBillFile: () => boolean;
  wasPendingUploadStarted: () => boolean;
  markPendingUploadStarted: () => void;
  setPendingAddress: (parts: UkAddressParts) => void;
  setPendingAddressFromUrl: (parts: UkAddressParts, widget?: WidgetQuoteContext) => void;
  claimPendingAddress: () => UkAddressParts | null;
  setPendingEnrichment: (fields: UkAddressParts, enrichment: QuoteSessionEnrichment) => void;
  claimPendingEnrichment: (fields: UkAddressParts) => QuoteSessionEnrichment | null;
  claimPendingWidgetContext: () => WidgetQuoteContext | null;
  hasPendingAddress: () => boolean;
  setPendingPlanContact: (contact: PendingPlanContact) => void;
  claimPendingPlanContact: () => PendingPlanContact | null;
}

const QuoteFlowContext = createContext<QuoteFlowContextType | undefined>(undefined);

export function QuoteFlowProvider({ children }: { children: ReactNode }) {
  const pendingFileRef = useRef<File | null>(null);
  const pendingBillDataRef = useRef<BillData | null>(null);
  const pendingEnrichmentRef = useRef<{ key: string; enrichment: QuoteSessionEnrichment } | null>(null);
  const pendingAddressRef = useRef<UkAddressParts | null>(null);
  const pendingWidgetRef = useRef<WidgetQuoteContext | null>(null);
  const pendingContactRef = useRef<PendingPlanContact | null>(null);
  const uploadStartedRef = useRef(false);
  const [, setVersion] = useState(0);

  const setPendingBillFile = useCallback((file: File) => {
    pendingFileRef.current = file;
    uploadStartedRef.current = false;
    setVersion((v) => v + 1);
  }, []);

  const claimPendingBillFile = useCallback(() => {
    const file = pendingFileRef.current;
    pendingFileRef.current = null;
    return file;
  }, []);

  const setPendingBillData = useCallback((data: BillData) => {
    pendingBillDataRef.current = data;
  }, []);

  const claimPendingBillData = useCallback(() => {
    const data = pendingBillDataRef.current;
    pendingBillDataRef.current = null;
    return data;
  }, []);

  const hasPendingBillFile = useCallback(() => pendingFileRef.current != null, []);

  const wasPendingUploadStarted = useCallback(() => uploadStartedRef.current, []);

  const markPendingUploadStarted = useCallback(() => {
    uploadStartedRef.current = true;
  }, []);

  const setPendingAddress = useCallback((parts: UkAddressParts) => {
    pendingAddressRef.current = parts;
    setVersion((v) => v + 1);
  }, []);

  const setPendingAddressFromUrl = useCallback((parts: UkAddressParts, widget?: WidgetQuoteContext) => {
    pendingAddressRef.current = parts;
    pendingWidgetRef.current = widget ?? null;
    setVersion((v) => v + 1);
  }, []);

  const claimPendingAddress = useCallback(() => {
    const parts = pendingAddressRef.current;
    pendingAddressRef.current = null;
    return parts;
  }, []);

  const setPendingEnrichment = useCallback((fields: UkAddressParts, enrichment: QuoteSessionEnrichment) => {
    pendingEnrichmentRef.current = { key: confirmAddressKey(fields), enrichment };
  }, []);

  const claimPendingEnrichment = useCallback((fields: UkAddressParts) => {
    const pending = pendingEnrichmentRef.current;
    pendingEnrichmentRef.current = null;
    if (!pending || pending.key !== confirmAddressKey(fields)) return null;
    return pending.enrichment;
  }, []);

  const claimPendingWidgetContext = useCallback(() => {
    const widget = pendingWidgetRef.current;
    pendingWidgetRef.current = null;
    return widget;
  }, []);

  const hasPendingAddress = useCallback(() => pendingAddressRef.current != null, []);

  const setPendingPlanContact = useCallback((contact: PendingPlanContact) => {
    pendingContactRef.current = contact;
    setVersion((v) => v + 1);
  }, []);

  const claimPendingPlanContact = useCallback(() => {
    const contact = pendingContactRef.current;
    pendingContactRef.current = null;
    return contact;
  }, []);

  return (
    <QuoteFlowContext.Provider
      value={{
        setPendingBillFile,
        claimPendingBillFile,
        setPendingBillData,
        claimPendingBillData,
        hasPendingBillFile,
        wasPendingUploadStarted,
        markPendingUploadStarted,
        setPendingAddress,
        setPendingAddressFromUrl,
        claimPendingAddress,
        setPendingEnrichment,
        claimPendingEnrichment,
        claimPendingWidgetContext,
        hasPendingAddress,
        setPendingPlanContact,
        claimPendingPlanContact,
      }}
    >
      {children}
    </QuoteFlowContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useQuoteFlow(): QuoteFlowContextType {
  const context = useContext(QuoteFlowContext);
  if (!context) {
    throw new Error('useQuoteFlow must be used within a QuoteFlowProvider');
  }
  return context;
}
